<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import googleCalendarAPI from 'dashboard/api/googleCalendar';

export default {
  components: {
    NextButton,
  },
  data() {
    return {
      connections: [],
      calendarsByConn: {},
      loading: false,
      connecting: '',
      popupTimer: null,
    };
  },
  computed: {
    ...mapGetters({ currentRole: 'getCurrentRole' }),
    isAdmin() {
      return this.currentRole === 'administrator';
    },
    personal() {
      return this.connections.find(c => c.mine && !c.shared);
    },
    shared() {
      return this.connections.find(c => c.shared);
    },
  },
  mounted() {
    this.fetchConnections();
    window.addEventListener('storage', this.handleStorageSignal);
  },
  beforeUnmount() {
    window.removeEventListener('storage', this.handleStorageSignal);
    if (this.popupTimer) clearInterval(this.popupTimer);
  },
  methods: {
    onConnected() {
      this.connecting = '';
      useAlert(this.$t('INTEGRATION_SETTINGS.GOOGLE_CALENDAR.CONNECTED'));
      this.fetchConnections();
    },
    handleStorageSignal(event) {
      if (event.key !== 'google_calendar_oauth' || !event.newValue) return;
      if (event.newValue.startsWith('GOOGLE_CALENDAR_CONNECTED')) {
        this.onConnected();
      } else {
        this.connecting = '';
      }
    },
    openOAuthPopup(url) {
      const width = 600;
      const height = 720;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        url,
        'google_calendar_oauth',
        `width=${width},height=${height},left=${left},top=${top}`
      );
      if (this.popupTimer) clearInterval(this.popupTimer);
      this.popupTimer = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(this.popupTimer);
          this.connecting = '';
          this.fetchConnections();
        }
      }, 800);
    },
    async fetchConnections() {
      this.loading = true;
      try {
        const { data } = await googleCalendarAPI.get();
        this.connections = data || [];
        this.connections.forEach(c => this.loadCalendars(c.id));
      } finally {
        this.loading = false;
      }
    },
    async loadCalendars(id) {
      try {
        const { data } = await googleCalendarAPI.calendars(id);
        this.calendarsByConn = { ...this.calendarsByConn, [id]: data || [] };
      } catch (error) {
        this.calendarsByConn = { ...this.calendarsByConn, [id]: [] };
      }
    },
    async setCalendar(conn, calendarId) {
      try {
        await googleCalendarAPI.update(conn.id, { calendar_id: calendarId });
        useAlert(this.$t('INTEGRATION_SETTINGS.GOOGLE_CALENDAR.SAVED'));
        this.fetchConnections();
      } catch (error) {
        useAlert(this.$t('INTEGRATION_SETTINGS.GOOGLE_CALENDAR.SAVE_ERROR'));
      }
    },
    async connect(kind) {
      this.connecting = kind;
      try {
        const { data } = await googleCalendarAPI.generateAuthorization(kind);
        this.openOAuthPopup(data.url);
      } catch (error) {
        this.connecting = '';
        useAlert(this.$t('INTEGRATION_SETTINGS.GOOGLE_CALENDAR.CONNECT_ERROR'));
      }
    },
    async remove(id) {
      try {
        await googleCalendarAPI.delete(id);
        this.fetchConnections();
      } catch (error) {
        useAlert(this.$t('INTEGRATION_SETTINGS.GOOGLE_CALENDAR.SAVE_ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full max-w-[60rem] gap-6 mx-auto py-6">
    <div class="min-w-0">
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ $t('INTEGRATION_SETTINGS.GOOGLE_CALENDAR.HEADER') }}
      </h1>
      <p class="mt-1 text-sm text-n-slate-10">
        {{ $t('INTEGRATION_SETTINGS.GOOGLE_CALENDAR.DESCRIPTION') }}
      </p>
    </div>

    <!-- Personal calendar -->
    <div class="flex flex-col gap-3 p-5 rounded-xl bg-n-solid-2">
      <div class="flex items-start justify-between gap-4">
        <div class="flex items-center gap-2 min-w-0">
          <span class="i-lucide-user text-n-slate-11 text-lg shrink-0" />
          <div class="min-w-0">
            <h2 class="text-base font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.GOOGLE_CALENDAR.PERSONAL.TITLE') }}
            </h2>
            <p class="text-xs text-n-slate-10">
              {{ $t('INTEGRATION_SETTINGS.GOOGLE_CALENDAR.PERSONAL.HINT') }}
            </p>
          </div>
        </div>
        <NextButton
          v-if="!personal"
          icon="i-lucide-circle-plus"
          :label="$t('INTEGRATION_SETTINGS.GOOGLE_CALENDAR.CONNECT')"
          :is-loading="connecting === 'personal'"
          sm
          class="shrink-0"
          @click="connect('personal')"
        />
      </div>
      <template v-if="personal">
        <div
          class="flex items-center justify-between gap-3 p-3 rounded-lg bg-n-alpha-black2"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span class="i-lucide-circle-check text-n-teal-9 shrink-0" />
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ personal.email }}
            </span>
          </div>
          <NextButton
            icon="i-lucide-trash-2"
            ruby
            faded
            xs
            @click="remove(personal.id)"
          />
        </div>
        <label
          v-if="(calendarsByConn[personal.id] || []).length"
          class="flex flex-col gap-1 text-sm text-n-slate-11"
        >
          {{ $t('INTEGRATION_SETTINGS.GOOGLE_CALENDAR.CALENDAR_LABEL') }}
          <select
            :value="personal.calendar_id"
            class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
            @change="setCalendar(personal, $event.target.value)"
          >
            <option
              v-for="cal in calendarsByConn[personal.id]"
              :key="cal.id"
              :value="cal.id"
            >
              {{ cal.summary }}
            </option>
          </select>
        </label>
      </template>
    </div>

    <!-- Shared calendar (admin) -->
    <div v-if="isAdmin" class="flex flex-col gap-3 p-5 rounded-xl bg-n-solid-2">
      <div class="flex items-start justify-between gap-4">
        <div class="flex items-center gap-2 min-w-0">
          <span class="i-lucide-users text-n-slate-11 text-lg shrink-0" />
          <div class="min-w-0">
            <h2 class="text-base font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.GOOGLE_CALENDAR.SHARED.TITLE') }}
            </h2>
            <p class="text-xs text-n-slate-10">
              {{ $t('INTEGRATION_SETTINGS.GOOGLE_CALENDAR.SHARED.HINT') }}
            </p>
          </div>
        </div>
        <NextButton
          v-if="!shared"
          icon="i-lucide-circle-plus"
          :label="$t('INTEGRATION_SETTINGS.GOOGLE_CALENDAR.CONNECT')"
          :is-loading="connecting === 'shared'"
          sm
          class="shrink-0"
          @click="connect('shared')"
        />
      </div>
      <template v-if="shared">
        <div
          class="flex items-center justify-between gap-3 p-3 rounded-lg bg-n-alpha-black2"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span class="i-lucide-circle-check text-n-teal-9 shrink-0" />
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ shared.email }}
            </span>
          </div>
          <NextButton
            icon="i-lucide-trash-2"
            ruby
            faded
            xs
            @click="remove(shared.id)"
          />
        </div>
        <label
          v-if="(calendarsByConn[shared.id] || []).length"
          class="flex flex-col gap-1 text-sm text-n-slate-11"
        >
          {{ $t('INTEGRATION_SETTINGS.GOOGLE_CALENDAR.CALENDAR_LABEL') }}
          <select
            :value="shared.calendar_id"
            class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
            @change="setCalendar(shared, $event.target.value)"
          >
            <option
              v-for="cal in calendarsByConn[shared.id]"
              :key="cal.id"
              :value="cal.id"
            >
              {{ cal.summary }}
            </option>
          </select>
        </label>
      </template>
    </div>
  </div>
</template>
